import { useContext, useEffect, useMemo, useState } from "react";
import { CaptionsState, LocalStorageKeys } from "../types";
import { GlobalContext } from "../components/GlobalContext";
import * as teams from "@microsoft/teams-js";

const useCaptions = (params?: { teamsContext?: teams.app.Context }) => {
  const { captions, setCaptions } = useContext(GlobalContext);
  const [loaded, setLoaded] = useState(false);

  const teamsMeetingId = useMemo(() => {
    return params?.teamsContext?.meeting?.id;
  }, [params?.teamsContext]);

  // Every 5s set captions last accessed
  useEffect(() => {
    const interval = setInterval(() => {
      localStorage.setItem(
        LocalStorageKeys.CAPTIONS_LAST_ACCESS,
        new Date().getTime().toString()
      );
    }, 5 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  // Load captions
  useEffect(() => {
    if (!teamsMeetingId) {
      return;
    }

    try {
      // Clear captions when meeting id changes
      const lastMeetingId = localStorage.getItem(LocalStorageKeys.MEETING_ID);
      if (!lastMeetingId || lastMeetingId !== teamsMeetingId) {
        localStorage.removeItem(LocalStorageKeys.CAPTIONS);
        localStorage.setItem(LocalStorageKeys.MEETING_ID, teamsMeetingId);
      }

      const json = localStorage.getItem(LocalStorageKeys.CAPTIONS);
      if (!json) {
        return;
      }

      const captionsLastAccess = localStorage.getItem(
        LocalStorageKeys.CAPTIONS_LAST_ACCESS
      );
      if (captionsLastAccess) {
        const now = new Date().getTime();
        const lastAccess = Number(captionsLastAccess);
        if (now - lastAccess > 5 * 60 * 1000) {
          return;
        }
      }

      setCaptions(JSON.parse(json) as CaptionsState);
    } finally {
      setLoaded(true);
    }
  }, [teamsMeetingId, setCaptions]);

  // Save captions
  useEffect(() => {
    if (!teamsMeetingId) {
      return;
    }

    if (!loaded) {
      return;
    }

    localStorage.setItem(LocalStorageKeys.CAPTIONS, JSON.stringify(captions));
  }, [teamsMeetingId, loaded, captions]);

  return [captions, setCaptions] as const;
};

export default useCaptions;
